import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Snowflake, Phone, ArrowRight,
  Star, Thermometer, Wrench, Shield, ChevronLeft, ChevronRight,
  MessageSquare, Send, User, Building, X, RefreshCw
} from 'lucide-react'

// Interface para TypeScript
interface Testimonial {
  id: string | number;
  name: string;
  company: string;
  text: string;
  rating: number;
  status: string;
  dateTime?: string;
  origin?: string;
}

// URL do Google Apps Script
const GOOGLE_TESTIMONIALS_SCRIPT_URL = import.meta.env.VITE_TESTIMONIALS_SCRIPT_URL;

// Constantes para animações (reduzidas para melhor performance)
const CAROUSEL_INTERVAL = 5000;
const MODAL_CLOSE_DELAY = 5000;

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [showTestimonialForm, setShowTestimonialForm] = useState(false)
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    company: '',
    testimonial: '',
    rating: 5
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')
  const [isPlaying, setIsPlaying] = useState(true)
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(false)
  const [testimonials, setTestimonials] = useState<Testimonial[]>([
    {
      id: 1,
      name: 'Maria Silva',
      company: 'Restaurante Bom Sabor',
      text: 'Excelente atendimento! A Tech Glacial instalou nossa câmara fria e o resultado superou expectativas.',
      rating: 5,
      status: 'publico'
    },
    {
      id: 2,
      name: 'João Santos',
      company: 'Indústria ABC',
      text: 'Profissionais competentes e pontualidade exemplar. Recomendo para qualquer empresa.',
      rating: 5,
      status: 'publico'
    },
    {
      id: 3,
      name: 'Ana Costa',
      company: 'Shopping Center XYZ',
      text: 'Manutenção impecável em todo nosso sistema de climatização. Parceria de longa data.',
      rating: 5,
      status: 'publico'
    }
  ]);

  // Refs
  const testimonialFormRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const submitTimeoutRef = useRef<NodeJS.Timeout>()

  // Scroll suave otimizado
  const handleLinkClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Memoizar serviços (dados estáticos)
  const services = useMemo(() => [
    {
      icon: Thermometer,
      title: 'Ar Condicionado',
      description: 'Instalação e manutenção de sistemas de ar condicionado residencial e comercial'
    },
    {
      icon: Snowflake,
      title: 'Refrigeração Industrial',
      description: 'Câmaras frigoríficas e sistemas de refrigeração para indústrias'
    },
    {
      icon: Wrench,
      title: 'Manutenção',
      description: 'Manutenção preventiva e corretiva com equipe especializada'
    },
    {
      icon: Shield,
      title: 'Projetos Personalizados',
      description: 'Soluções sob medida para suas necessidades específicas'
    }
  ], []);

  // Processar dados dos depoimentos (memoizado)
  const processTestimonialsData = useCallback((data: Testimonial[]) => {
    if (!Array.isArray(data)) return;

    const mappedTestimonials = data.map((item) => ({
      id: item.id || Math.random(),
      name: item.name || '',
      company: item.company || '',
      text: item.text || '',
      rating: parseInt(item.rating?.toString() || '5'),
      status: item.status || 'publico',
      dateTime: item.dateTime,
      origin: item.origin
    }));

    if (mappedTestimonials.length > 0) {
      setTestimonials(mappedTestimonials);
    }
  }, []);

  // Buscar depoimentos (otimizado)
  const fetchTestimonials = useCallback(async () => {
    if (!GOOGLE_TESTIMONIALS_SCRIPT_URL) return;

    setIsLoadingTestimonials(true);
    
    try {
      const response = await fetch(`${GOOGLE_TESTIMONIALS_SCRIPT_URL}?action=testimonials&t=${Date.now()}`, {
        method: 'GET',
        mode: 'cors'
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          processTestimonialsData(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar depoimentos:', error);
    } finally {
      setIsLoadingTestimonials(false);
    }
  }, [processTestimonialsData]);

  // Filtrar depoimentos públicos (memoizado)
  const publicTestimonials = useMemo(() => 
    testimonials.filter(t => t.status && t.status.toLowerCase() === 'publico'),
    [testimonials]
  );

  // Carregar depoimentos ao montar
  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  // Reset do índice quando lista mudar
  useEffect(() => {
    if (currentTestimonial >= publicTestimonials.length && publicTestimonials.length > 0) {
      setCurrentTestimonial(0);
    }
  }, [publicTestimonials.length, currentTestimonial]);

  // Auto-rotate do carrossel (otimizado)
  useEffect(() => {
    if (publicTestimonials.length <= 1 || !isPlaying) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) =>
        prev === publicTestimonials.length - 1 ? 0 : prev + 1
      )
    }, CAROUSEL_INTERVAL)

    return () => clearInterval(interval)
  }, [publicTestimonials.length, isPlaying])

  // Navegação do carrossel (memoizado)
  const nextTestimonial = useCallback(() => {
    setCurrentTestimonial((prev) =>
      prev === publicTestimonials.length - 1 ? 0 : prev + 1
    );
  }, [publicTestimonials.length]);

  const prevTestimonial = useCallback(() => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? publicTestimonials.length - 1 : prev - 1
    );
  }, [publicTestimonials.length]);

  // Controle de teclado (memoizado)
  const handleCarouselKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        prevTestimonial();
        break;
      case 'ArrowRight':
        e.preventDefault();
        nextTestimonial();
        break;
      case ' ':
        e.preventDefault();
        setIsPlaying(prev => !prev);
        break;
    }
  }, [nextTestimonial, prevTestimonial]);

  // Fechar modal (memoizado)
  const closeTestimonialForm = useCallback(() => {
    setShowTestimonialForm(false);
    setSubmitMessage('');
    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, []);

  // Submeter depoimento (otimizado)
  const submitTestimonial = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    // Limpar timeout anterior se existir
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }

    try {
      if (!GOOGLE_TESTIMONIALS_SCRIPT_URL) {
        throw new Error('URL do Google Apps Script não configurada');
      }

      if (!testimonialForm.name.trim() || !testimonialForm.testimonial.trim()) {
        throw new Error('Nome e depoimento são obrigatórios');
      }

      const testimonialData = {
        Nome: testimonialForm.name.trim(),
        Empresa: testimonialForm.company.trim() || '',
        Depoimento: testimonialForm.testimonial.trim(),
        Avaliacao: testimonialForm.rating,
        Origem: 'Site - Formulário Depoimentos'
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const response = await fetch(GOOGLE_TESTIMONIALS_SCRIPT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(testimonialData),
          mode: 'cors',
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const responseText = await response.text();
          let result;
          
          try {
            result = JSON.parse(responseText);
          } catch {
            result = { success: true };
          }

          if (result.success !== false) {
            setSubmitMessage('Depoimento enviado com sucesso! Obrigado por compartilhar sua experiência.||Seu depoimento está em análise e será publicado em breve.');
            setTestimonialForm({ name: '', company: '', testimonial: '', rating: 5 });
            
            setTimeout(() => fetchTestimonials(), 2000);
            submitTimeoutRef.current = setTimeout(closeTestimonialForm, MODAL_CLOSE_DELAY);
            return;
          }
        }
      } catch {
        clearTimeout(timeoutId);
        
        await fetch(GOOGLE_TESTIMONIALS_SCRIPT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(testimonialData),
          mode: 'no-cors'
        });

        setSubmitMessage('Depoimento enviado com sucesso!||Aguarde a análise. Será publicado em breve.');
        setTestimonialForm({ name: '', company: '', testimonial: '', rating: 5 });
        
        setTimeout(() => fetchTestimonials(), 3000);
        submitTimeoutRef.current = setTimeout(closeTestimonialForm, MODAL_CLOSE_DELAY);
      }

    } catch (error: unknown) {
      let errorMessage = 'Erro ao enviar depoimento. ';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage += 'Tempo esgotado. Tente novamente.';
        } else {
          errorMessage += error.message;
        }
      }
      
      setSubmitMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [testimonialForm, closeTestimonialForm, fetchTestimonials]);

  // Gerenciar foco do modal
  useEffect(() => {
    if (showTestimonialForm) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      setTimeout(() => testimonialFormRef.current?.focus(), 100);
    }
  }, [showTestimonialForm]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  // Depoimento atual (memoizado)
  const currentTestimonialData = useMemo(() => 
    publicTestimonials[currentTestimonial],
    [publicTestimonials, currentTestimonial]
  );

  return (
    <div className="pt-20">
      {/* Skip to main content */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg z-50"
      >
        Pular para o conteúdo principal
      </a>

      {/* Hero Section - Sem animações complexas */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-700"
        aria-labelledby="hero-heading"
      >
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <Snowflake className="absolute top-20 left-20 h-32 w-32 text-white" />
          <Snowflake className="absolute bottom-20 right-20 h-24 w-24 text-white" />
          <Snowflake className="absolute top-1/2 left-1/3 h-16 w-16 text-white" />
        </div>

        <div id="main-content" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 id="hero-heading" className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Soluções em Refrigeração e Climatização
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Tecnologia de ponta, qualidade garantida e atendimento para
            empresas e residências em todo o Brasil
          </p>
          <nav aria-label="Navegação principal da página inicial" className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/budget"
              onClick={handleLinkClick}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 focus:ring-4 focus:ring-cyan-300 focus:outline-none"
              aria-label="Ir para página de orçamento"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              <span>Solicitar Orçamento</span>
            </Link>
            <Link
              to="/servicos"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 focus:ring-4 focus:ring-white focus:outline-none"
              aria-label="Ver página de serviços"
            >
              <span>Nossos Serviços</span>
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </nav>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50" aria-labelledby="services-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-16">
            <h2 id="services-heading" className="text-4xl font-bold text-gray-900 mb-4">
              Nossos Serviços
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Oferecemos soluções completas em refrigeração e climatização
              com tecnologia de ponta e equipe altamente qualificada
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <article
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="bg-gradient-to-r from-blue-600 to-cyan-500 w-12 h-12 rounded-lg flex items-center justify-center mb-4" aria-hidden="true">
                  <service.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600">
                  {service.description}
                </p>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/servicos"
              className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-3 rounded-lg hover:shadow-lg transition-shadow duration-200 inline-flex items-center space-x-2 focus:ring-4 focus:ring-blue-300 focus:outline-none"
            >
              <span>Ver Todos os Serviços</span>
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20 bg-white" aria-labelledby="testimonials-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-16">
            <h2 id="testimonials-heading" className="text-4xl font-bold text-gray-900 mb-4">
              O Que Nossos Clientes Dizem
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Depoimentos reais de quem confia na Tech Glacial
            </p>

            <nav aria-label="Ações de depoimentos" className="flex justify-center gap-4 mb-12 flex-wrap">
              <button
                onClick={() => {
                  previousFocusRef.current = document.activeElement as HTMLElement;
                  setShowTestimonialForm(true);
                }}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-shadow duration-200 inline-flex items-center space-x-2 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                aria-label="Abrir formulário para deixar depoimento"
              >
                <MessageSquare className="h-5 w-5" aria-hidden="true" />
                <span>Deixe seu Depoimento</span>
              </button>

              {import.meta.env.DEV && (
                <>
                  <button
                    onClick={fetchTestimonials}
                    disabled={isLoadingTestimonials}
                    className="bg-gray-600 text-white px-4 py-3 rounded-lg hover:shadow-lg transition-shadow duration-200 text-sm focus:ring-4 focus:ring-gray-300 focus:outline-none disabled:opacity-50 flex items-center space-x-2"
                    aria-label="Recarregar depoimentos"
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoadingTestimonials ? 'animate-spin' : ''}`} />
                    <span>Recarregar ({publicTestimonials.length})</span>
                  </button>
                </>
              )}
            </nav>
          </header>

          {isLoadingTestimonials && (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="inline-flex items-center space-x-2 text-blue-600">
                <RefreshCw className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Carregando depoimentos...</span>
              </div>
            </div>
          )}

          {!isLoadingTestimonials && publicTestimonials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg mb-4">
                Ainda não há depoimentos públicos. Seja o primeiro a deixar o seu!
              </p>
              <button
                onClick={() => setShowTestimonialForm(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:ring-4 focus:ring-blue-300 focus:outline-none"
              >
                Deixar Depoimento
              </button>
            </div>
          ) : !isLoadingTestimonials && (
            <div
              className="relative max-w-4xl mx-auto"
              ref={carouselRef}
              role="region"
              aria-label="Carrossel de depoimentos de clientes"
              aria-live="polite"
              aria-atomic="false"
              tabIndex={0}
              onKeyDown={handleCarouselKeyDown}
            >
              <div className="sr-only" aria-live="polite">
                Exibindo depoimento {currentTestimonial + 1} de {publicTestimonials.length}.
                Use as setas esquerda e direita para navegar.
                Pressione espaço para {isPlaying ? 'pausar' : 'retomar'} a reprodução automática.
              </div>

              <div className="relative min-h-64 flex items-center justify-center">
                {currentTestimonialData && (
                  <AnimatePresence mode="wait">
                    <motion.article
                      key={currentTestimonial}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-gray-50 p-8 rounded-xl max-w-3xl w-full mx-4"
                    >
                      <div 
                        className="flex items-center justify-center mb-4" 
                        role="img"
                        aria-label={`Avaliação de ${currentTestimonialData.rating} de 5 estrelas`}
                      >
                        {[...Array(currentTestimonialData.rating)].map((_, i) => (
                          <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" aria-hidden="true" />
                        ))}
                      </div>
                      <blockquote className="text-gray-700 text-lg mb-6 text-center">
                        <p className="italic">"{currentTestimonialData.text}"</p>
                      </blockquote>
                      <footer className="text-center">
                        <cite className="font-semibold text-gray-900 text-lg not-italic">
                          {currentTestimonialData.name}
                        </cite>
                        {currentTestimonialData.company && (
                          <p className="text-gray-600">
                            {currentTestimonialData.company}
                          </p>
                        )}
                      </footer>
                    </motion.article>
                  </AnimatePresence>
                )}
              </div>

              {publicTestimonials.length > 1 && (
                <>
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="absolute top-4 right-4 bg-white hover:bg-gray-100 p-2 rounded-full shadow-lg transition-colors duration-200 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                    aria-label={isPlaying ? 'Pausar reprodução automática de depoimentos' : 'Retomar reprodução automática de depoimentos'}
                    aria-pressed={isPlaying}
                  >
                    <span aria-hidden="true">{isPlaying ? '⏸' : '▶'}</span>
                  </button>

                  <button
                    onClick={prevTestimonial}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-colors duration-200 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                    aria-label={`Ir para depoimento anterior. Depoimento atual: ${currentTestimonial + 1} de ${publicTestimonials.length}`}
                  >
                    <ChevronLeft className="h-6 w-6 text-gray-600" />
                  </button>

                  <button
                    onClick={nextTestimonial}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-colors duration-200 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                    aria-label={`Ir para próximo depoimento. Depoimento atual: ${currentTestimonial + 1} de ${publicTestimonials.length}`}
                  >
                    <ChevronRight className="h-6 w-6 text-gray-600" />
                  </button>

                  <nav className="flex justify-center mt-8 space-x-2" role="tablist" aria-label="Navegação de depoimentos">
                    {publicTestimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`h-3 rounded-full transition-all duration-200 focus:ring-4 focus:ring-blue-300 focus:outline-none ${
                          index === currentTestimonial
                            ? 'bg-blue-600 w-8'
                            : 'bg-gray-300 hover:bg-gray-400 w-3'
                        }`}
                        role="tab"
                        aria-selected={index === currentTestimonial}
                        aria-label={`Ir para depoimento ${index + 1} de ${publicTestimonials.length}`}
                        aria-controls={`testimonial-${index}`}
                      />
                    ))}
                  </nav>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Testimonial Form Modal */}
      <AnimatePresence>
        {showTestimonialForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="testimonial-modal-title"
            onClick={(e) => e.target === e.currentTarget && closeTestimonialForm()}
            onKeyDown={(e) => e.key === 'Escape' && closeTestimonialForm()}
          >
            <div
           className="bg-white rounded-xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
              ref={testimonialFormRef}
              tabIndex={-1}
            >
              <header className="flex justify-between items-center mb-6">
                <h3 id="testimonial-modal-title" className="text-2xl font-bold text-gray-900">
                  Conte sua Experiência
                </h3>
                <button
                  onClick={closeTestimonialForm}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:ring-4 focus:ring-gray-300 focus:outline-none"
                  aria-label="Fechar formulário de depoimento"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </header>

              {submitMessage && (
                <div role="alert" aria-live="polite">
                  {submitMessage.split('||').map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg mb-3 ${
                        idx === 0
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                      }`}
                    >
                      {msg}
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={submitTestimonial} className="space-y-4">
                <div>
                  <label htmlFor="testimonial-name" className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="h-4 w-4 inline mr-1" aria-hidden="true" />
                    Seu Nome <span aria-label="obrigatório">*</span>
                  </label>
                  <input
                    id="testimonial-name"
                    type="text"
                    required
                    value={testimonialForm.name}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="João Silva"
                    disabled={isSubmitting}
                    aria-required="true"
                  />
                </div>

                <div>
                  <label htmlFor="testimonial-company" className="block text-sm font-medium text-gray-700 mb-1">
                    <Building className="h-4 w-4 inline mr-1" aria-hidden="true" />
                    Empresa (opcional)
                  </label>
                  <input
                    id="testimonial-company"
                    type="text"
                    value={testimonialForm.company}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, company: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Minha Empresa Ltda"
                    disabled={isSubmitting}
                  />
                </div>

                <fieldset disabled={isSubmitting}>
                  <legend className="block text-sm font-medium text-gray-700 mb-2">
                    Avaliação <span aria-label="obrigatório">*</span>
                  </legend>
                  <div className="flex space-x-1" role="group" aria-label="Selecione uma avaliação de 1 a 5 estrelas">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setTestimonialForm({ ...testimonialForm, rating: star })}
                        className={`p-1 transition-colors duration-200 focus:ring-4 focus:ring-yellow-300 focus:outline-none rounded ${
                          star <= testimonialForm.rating
                            ? 'text-yellow-400'
                            : 'text-gray-300 hover:text-yellow-300'
                        }`}
                        aria-label={`Avaliar com ${star} ${star === 1 ? 'estrela' : 'estrelas'}`}
                        aria-pressed={star <= testimonialForm.rating}
                        disabled={isSubmitting}
                      >
                        <Star className="h-6 w-6 fill-current" />
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-1" role="status" aria-live="polite">
                    Avaliação selecionada: {testimonialForm.rating} {testimonialForm.rating === 1 ? 'estrela' : 'estrelas'}
                  </p>
                </fieldset>

                <div>
                  <label htmlFor="testimonial-text" className="block text-sm font-medium text-gray-700 mb-1">
                    Seu Depoimento <span aria-label="obrigatório">*</span>
                  </label>
                  <textarea
                    id="testimonial-text"
                    required
                    rows={4}
                    value={testimonialForm.testimonial}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, testimonial: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Conte como foi sua experiência com a Tech Glacial..."
                    disabled={isSubmitting}
                    aria-required="true"
                    aria-describedby="testimonial-help"
                  />
                  <p id="testimonial-help" className="text-sm text-gray-600 mt-1">
                    Descreva sua experiência com nossos serviços. Seu depoimento será analisado antes da publicação.
                  </p>
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={closeTestimonialForm}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 focus:ring-4 focus:ring-gray-300 focus:outline-none disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !testimonialForm.name.trim() || !testimonialForm.testimonial.trim()}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-3 rounded-lg hover:shadow-lg transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" aria-hidden="true" />
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" aria-hidden="true" />
                        <span>Enviar Depoimento</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800 text-white" aria-labelledby="cta-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="cta-heading" className="text-4xl font-bold mb-4">
            Pronto para Começar seu Projeto?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Entre em contato conosco e receba um orçamento personalizado
            para suas necessidades de refrigeração e climatização
          </p>
          <nav aria-label="Ações finais" className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contato"
              className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center justify-center space-x-2 focus:ring-4 focus:ring-white focus:outline-none"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              <span>Falar com Especialista</span>
            </Link>
            <Link
              to="/portfolio"
              onClick={handleLinkClick}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center justify-center space-x-2 focus:ring-4 focus:ring-white focus:outline-none"
            >
              <span>Ver Nossos Projetos</span>
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </nav>
        </div>
      </section>
    </div>
  )
}

export default Home